import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconBell,
    IconSearch,
    IconCheck,
    IconChecks,
    IconShoppingBag,
    IconUserPlus,
    IconCalendar,
    IconUser,
    IconInfoCircle,
    IconExternalLink,
    IconArrowLeft
} from '@tabler/icons';

import { motion } from 'framer-motion';
import api from '../../../../services/api';
import { useHistory, useParams } from 'react-router-dom';

const MotionCard = motion(Card);

const TYPE_CONFIG = {
    product: {
        label: 'Produto',
        color: '#16a34a',
        gradient: 'linear-gradient(135deg,#16a34a,#22c55e)',
        icon: IconShoppingBag
    },
    lead: {
        label: 'Lead',
        color: '#2563eb',
        gradient: 'linear-gradient(135deg,#2563eb,#60a5fa)',
        icon: IconUserPlus
    },
    calendar: {
        label: 'Calendário',
        color: '#ea580c',
        gradient: 'linear-gradient(135deg,#ea580c,#fb923c)',
        icon: IconCalendar
    },
    user: {
        label: 'Usuário',
        color: '#7c3aed',
        gradient: 'linear-gradient(135deg,#7c3aed,#a855f7)',
        icon: IconUser
    },
    system: {
        label: 'Sistema',
        color: '#64748b',
        gradient: 'linear-gradient(135deg,#64748b,#94a3b8)',
        icon: IconInfoCircle
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminNotification = () => {
    const theme = useTheme();

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [appId, setAppId] = useState(null);
    const history = useHistory();

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadNotifications();
    }, [appId]);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const response = await api.get(`v1/apps/${appId}/notifications/`);
            setNotifications(response.data?.notifications || []);
            setUnreadCount(response.data?.unread_count || 0);

            //exemplo retorno:
            // {
            //     "notifications": [
            //         {
            //             "id": "3906ebda-4b82-410f-aab3-bec6676e0558",
            //             "type": "calendar",
            //             "message": "Evento hoje: Reuniao sobre o sistema às 16:00",
            //             "link": null,
            //             "read": false,
            //             "created_at": "2026-05-24T16:34:07.129020Z"
            //         },
            //         {
            //             "id": "9b30a431-d2c8-4636-94fb-3345b3138ba9",
            //             "type": "calendar",
            //             "message": "Evento hoje: Reunião com Edson às 16:00",
            //             "link": null,
            //             "read": false,
            //             "created_at": "2026-05-24T16:28:57.792416Z"
            //         },
            //         {
            //             "id": "075ed85a-f797-48d5-8095-487f511e5112",
            //             "type": "product",
            //             "message": "Novo produto adicionado: Computador",
            //             "link": null,
            //             "read": false,
            //             "created_at": "2026-05-24T16:15:09.849555Z"
            //         },
            //         {
            //             "id": "585bfba2-f15c-4e58-86cc-d6df044ecddd",
            //             "type": "system",
            //             "message": "Novo usuário criado: Angelo Mazarin",
            //             "link": null,
            //             "read": false,
            //             "created_at": "2026-05-24T15:28:24.752518Z"
            //         }
            //     ],
            //     "unread_count": 4
            // }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar notificações.' });
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (notificationId) => {
        try {
            await api.patch(`v1/apps/${appId}/notifications/${notificationId}/`);
            setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error(error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.post(`v1/apps/${appId}/notifications/mark-all-read/`);
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
            setStatus({ type: 'success', message: 'Todas as notificações marcadas como lidas.' });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao marcar notificações.' });
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return notifications;
        const term = search.toLowerCase();
        return notifications.filter((n) => n.message?.toLowerCase().includes(term));
    }, [search, notifications]);

    return (
        <Box>
            {/* HERO */}

            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '28px',
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    mt: 2,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#1e293b 100%)'
                            : 'linear-gradient(135deg,#eff6ff 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.08)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(99,102,241,0.15))',
                        top: -120,
                        right: -80,
                        filter: 'blur(40px)'
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconBell size={14} />} label="Central de Notificações" sx={{ mb: 2, fontWeight: 700 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2rem', md: '3rem' },
                                letterSpacing: '-2px',
                                background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Notificações
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip
                                label={`${unreadCount} não lida${unreadCount > 1 ? 's' : ''}`}
                                color="primary"
                                sx={{ fontWeight: 700, fontSize: 13 }}
                            />
                        )}
                    </Box>

                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                        Alertas automáticos do sistema: novos leads, produtos, eventos e mais.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <OutlinedInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar notificação..."
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            }
                            sx={{
                                flex: 1,
                                minWidth: 280,
                                height: 56,
                                borderRadius: '18px',
                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                            }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<IconChecks size={18} />}
                            onClick={handleMarkAllRead}
                            disabled={unreadCount === 0}
                            sx={{ height: 56, px: 3, borderRadius: '18px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Marcar tudo como lido
                        </Button>
                    </Box>
                </Box>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* LISTA */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
                    <IconBell size={48} opacity={0.3} />
                    <Typography variant="h4" sx={{ mt: 2, fontWeight: 700, opacity: 0.5 }}>
                        Nenhuma notificação
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filtered.map((notification, index) => {
                        const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.system;
                        const Icon = config.icon;

                        return (
                            <MotionCard
                                key={notification.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.03 }}
                                sx={{
                                    borderRadius: '20px',
                                    border: notification.read
                                        ? theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255,255,255,0.06)'
                                            : '1px solid rgba(15,23,42,0.06)'
                                        : `1px solid ${config.color}44`,
                                    background: notification.read
                                        ? theme.palette.mode === 'dark'
                                            ? '#111827'
                                            : '#fff'
                                        : theme.palette.mode === 'dark'
                                          ? `${config.color}11`
                                          : `${config.color}08`,
                                    boxShadow: notification.read ? 'none' : `0 4px 20px ${config.color}22`,
                                    opacity: notification.read ? 0.75 : 1
                                }}
                            >
                                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                background: notification.read
                                                    ? theme.palette.mode === 'dark'
                                                        ? '#1e293b'
                                                        : '#f1f5f9'
                                                    : config.gradient,
                                                flexShrink: 0
                                            }}
                                        >
                                            <Icon size={22} color={notification.read ? config.color : '#fff'} />
                                        </Avatar>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={config.label}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: 11,
                                                        background: config.gradient,
                                                        color: '#fff',
                                                        height: 20
                                                    }}
                                                />
                                                {!notification.read && <Badge variant="dot" color="primary" sx={{ ml: 0.5 }} />}
                                            </Box>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: notification.read ? 400 : 600,
                                                    color: 'text.primary',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {notification.message}
                                            </Typography>

                                            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                                {formatDate(notification.created_at)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                            {notification.link && (
                                                <Tooltip title="Abrir">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => (window.location.href = notification.link)}
                                                        sx={{ borderRadius: '10px' }}
                                                    >
                                                        <IconExternalLink size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {!notification.read && (
                                                <Tooltip title="Marcar como lida">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMarkRead(notification.id)}
                                                        sx={{
                                                            borderRadius: '10px',
                                                            color: config.color,
                                                            background: `${config.color}15`,
                                                            '&:hover': { background: `${config.color}25` }
                                                        }}
                                                    >
                                                        <IconCheck size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </MotionCard>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default AdminNotification;
